import React from 'react';

import { ds } from '@/components/lib/design.system';

import { SizedBox } from '../SizedBox';
import { StyleVariant } from '../TextComponent';
import { Text } from '../TextComponent';
import { Box } from '../lib/components';
import { DesignSystemColor } from '../lib/theme';
import { TiebreakerScore, TiebreakerScoreOffset, getTiebreakerSize } from './TiebreakerScore';
import { PeriodScore } from './types';

export type ScoreColumnTheme = {
    minWidth?: number;
    columnAlign?: 'center' | 'flex-end';
    sizedBoxDimensions: { top: number; middle: number };
    periodLabelVariant: StyleVariant;
    scoreVariant: StyleVariant;
    totalScoreVariant: StyleVariant;
    subScoreVariant?: StyleVariant;
    tiebreakerScoreOffset?: TiebreakerScoreOffset;
};

/**
 * Single discriminator that drives score color resolution:
 *  - 'normal': both scores neutral300 (Classic, Modern non-active periods)
 *  - 'total': both scores white (final score columns / 'T')
 *  - 'active': scores white, header neutral (Modern live in-progress period)
 *  - 'winner': higher score white, lower neutral300 (Modern final breakdown)
 */
export type ScoreEmphasis = 'normal' | 'total' | 'active' | 'winner';

export type ScoreColumnProps = PeriodScore & {
    theme: ScoreColumnTheme;
    periodLabel: string;
    emphasis?: ScoreEmphasis;
    textColor?: DesignSystemColor;
};

const getScoreColor = (
    emphasis: ScoreEmphasis,
    score: number | string | undefined,
    opponent: number | string | undefined
): DesignSystemColor => {
    if (emphasis === 'total' || emphasis === 'active') return ds.colorToken.text.primary;
    if (emphasis === 'winner' && typeof score === 'number' && typeof opponent === 'number' && score > opponent)
        return ds.colorToken.text.primary;
    return 'neutral300';
};

export const ScoreColumn = ({
    team1Score,
    team2Score,
    team1SubScore,
    team2SubScore,
    theme,
    periodLabel,
    emphasis = 'normal',
    textColor = 'neutral400',
}: ScoreColumnProps) => {
    const {
        minWidth = 16,
        columnAlign = 'flex-end',
        sizedBoxDimensions,
        periodLabelVariant,
        scoreVariant,
        totalScoreVariant,
        subScoreVariant,
        tiebreakerScoreOffset,
    } = theme;
    const labelColor: DesignSystemColor = emphasis === 'total' ? ds.colorToken.text.primary : textColor;
    const mainScoreVariant = emphasis === 'total' ? totalScoreVariant : scoreVariant;
    const smallScoreVariant = subScoreVariant ?? periodLabelVariant;
    // Both rows share one box size so a single-digit tie-break sits centered under a double-digit one.
    const tiebreakerSize = getTiebreakerSize(team1SubScore, team2SubScore);

    const renderScore = (
        score: number | string | undefined,
        subScore: number | undefined,
        color: DesignSystemColor
    ) => {
        const main = (
            <Text
                variant={mainScoreVariant}
                color={color}
                numberOfLines={1}
                textAlign={tiebreakerScoreOffset ? 'center' : undefined}
            >
                {score ?? '-'}
            </Text>
        );

        if (!tiebreakerScoreOffset || subScore === undefined) return main;

        return (
            <Box position="relative" width="100%">
                {main}
                <TiebreakerScore
                    score={subScore}
                    size={tiebreakerSize}
                    variant={smallScoreVariant}
                    offset={tiebreakerScoreOffset}
                />
            </Box>
        );
    };

    return (
        <Box minWidth={minWidth} width={columnAlign === 'center' ? minWidth : undefined} alignItems={columnAlign}>
            <Text variant={periodLabelVariant} color={labelColor}>
                {periodLabel}
            </Text>
            <SizedBox value={sizedBoxDimensions.top} />
            {renderScore(team1Score, team1SubScore, getScoreColor(emphasis, team1Score, team2Score))}
            <SizedBox value={sizedBoxDimensions.middle} />
            {renderScore(team2Score, team2SubScore, getScoreColor(emphasis, team2Score, team1Score))}
        </Box>
    );
};
